import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, Mail, ArrowRight, ArrowLeft, ShieldAlert, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext'; 
import TamkeenLogo from '../components/TamkeenLogo';
import LanguageToggle from '../components/LanguageToggle';

// استيراد أدوات المصادقة وقاعدة البيانات
import { db, auth } from '../firebase'; // تأكد من تصدير auth من ملف إعدادات firebase
import { signInWithEmailAndPassword } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const { login } = useAuth();
  const navigate = useNavigate();
  const { t, language } = useLanguage();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // 1. المصادقة الآمنة عبر Firebase Auth بدلاً من الاستعلام المكشوف
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;
      
      // 2. استخراج التوكن الأمني المشفر لتمريره لـ AuthContext
      const authToken = await firebaseUser.getIdToken();

      // 3. التحقق من الإدارة المركزية باستخدام UID مشفر أو إيميل بعد المصادقة
      if (firebaseUser.email === 'admin@tamkeen.ma') {
        login({ role: 'admin', name: 'الإدارة المركزية' }, authToken);
        navigate('/admin');
        setIsLoading(false);
        return;
      }

      // 4. جلب بيانات الإطار من Firestore بناءً على المعرف الآمن (UID)
      // نفترض هنا أن وثيقة الإطار في Firestore تحمل نفس الـ UID الخاص بـ Firebase Auth
      const educatorDocRef = doc(db, 'educators', firebaseUser.uid);
      const educatorDocSnap = await getDoc(educatorDocRef);

      if (educatorDocSnap.exists()) {
        const foundEducator = educatorDocSnap.data();

        if (foundEducator.status !== 'نشط') {
          setError(t('login.errorLocked'));
          // تسجيل خروج فوري من Firebase Auth إذا كان الحساب غير نشط
          await auth.signOut();
        } else {
          login({ 
            role: 'educator', 
            id: firebaseUser.uid,
            name: foundEducator.name, 
            workshop: foundEducator.workshop 
          }, authToken);
          navigate('/workshop');
        }
      } else {
        // إذا تمت المصادقة لكن لم يتم العثور على ملف تعريف في قاعدة البيانات
        await auth.signOut();
        setError(t('login.errorInvalid'));
      }
    } catch (err) {
      console.error("Authentication Error:", err);
      // توحيد رسالة الخطأ لأسباب أمنية (لكي لا يعرف المخترق إذا كان الخطأ في الإيميل أم كلمة المرور)
      setError("بيانات الدخول غير صحيحة أو يوجد مشكل في الاتصال.");
    } finally {
      setIsLoading(false);
    }
  };

  // ... (الجزء الخاص بـ الواجهة الرسومية UI والـ JSX يبقى كما هو تماماً لعدم وجود ثغرات فيه)
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center font-sans relative overflow-hidden p-4">
      <LanguageToggle /> 

      <div className="absolute top-[-10%] start-[-5%] w-[500px] h-[500px] bg-indigo-500/10 rounded-full filter blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-[-10%] end-[-5%] w-[600px] h-[600px] bg-emerald-500/10 rounded-full filter blur-3xl pointer-events-none"></div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md bg-white/80 backdrop-blur-xl border border-white p-8 md:p-10 rounded-3xl shadow-2xl relative z-10">
        <div className="flex flex-col items-center mb-8 text-center">
          <TamkeenLogo className="w-20 h-20 mb-4 drop-shadow-md" />
          <h1 className="text-2xl font-extrabold text-slate-800">{t('login.title')}</h1>
          <p className="text-sm font-bold text-slate-500 mt-2">{t('login.subtitle')}</p>
        </div>

        {error && (
          <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} className="mb-6 bg-red-50 border border-red-100 text-red-600 p-4 rounded-xl text-sm font-bold flex items-center gap-3">
            <ShieldAlert className="w-5 h-5 shrink-0" /> {error}
          </motion.div>
        )}

        <form onSubmit={handleLogin} className="space-y-5">
          <div>
            <label className="block text-sm font-bold text-slate-700 mb-2">{t('login.email')}</label>
            <div className="relative">
              <Mail className="absolute start-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input 
                type="email" 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl ps-12 pe-4 py-3.5 text-sm text-slate-800 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all font-sans" 
                placeholder="email@tamkeen.ma"
                required 
                disabled={isLoading}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-bold text-slate-700 mb-2">{t('login.password')}</label>
            <div className="relative">
              <Lock className="absolute start-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input 
                type="password" 
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl ps-12 pe-4 py-3.5 text-sm text-slate-800 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all font-sans tracking-widest text-start" 
                placeholder="••••••••"
                required 
                disabled={isLoading}
              />
            </div>
          </div>

          <button 
            type="submit" 
            disabled={isLoading}
            className="w-full py-4 bg-slate-900 hover:bg-slate-800 disabled:bg-slate-600 text-white rounded-xl font-bold text-lg flex items-center justify-center gap-2 shadow-xl shadow-slate-900/20 transition-all mt-6"
          >
            {isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                {t('login.submit')} 
                {language === 'ar' ? <ArrowLeft className="w-5 h-5" /> : <ArrowRight className="w-5 h-5" />}
              </>
            )}
          </button>
        </form>

        <div className="mt-8 text-center border-t border-slate-100 pt-6">
          <p className="text-xs font-bold text-slate-400 leading-relaxed">{t('login.forgot')}</p>
        </div>
      </motion.div>
    </div>
  );
};

export default LoginPage;